import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import Select from '../../ui/Select/Select'
import { Checkbox } from '../../ui'
import {
  ALL_MONTHS, DAYS_OF_WEEK, HOUR_OPTIONS, MONTH_DAYS, MONTH_NAMES,
  buildCron, buildCronSummary, parseCron
} from '../cronUtils'

const CHECKBOX_LABEL_CLASS = 'tw:flex! tw:items-center tw:gap-2 tw:cursor-pointer tw:m-0!'
const CHECKBOX_TEXT_CLASS = 'tw:text-sm tw:text-gray-900'

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Tous les jours' },
  { value: 'weekly', label: 'Certains jours de la semaine' },
  { value: 'monthly', label: 'Certains jours du mois' }
]

const toValues = options => (options || []).map(option => option.value)
const fromValues = (numbers, options) =>
  options.filter(option => numbers.includes(option.value))

const CustomCronBuilder = ({ initialCron, onChange }) => {
  const parsed = useMemo(() => parseCron(initialCron), [])
  const isFirstRender = useRef(true)

  const [frequency, setFrequency] = useState(parsed?.frequency || 'daily')
  const [hours, setHours] = useState(parsed?.hours || [7])
  const [weekDays, setWeekDays] = useState(parsed?.weekDays || [1])
  const [monthDays, setMonthDays] = useState(parsed?.monthDays || [1])
  const [months, setMonths] = useState(parsed?.months || ALL_MONTHS)
  const [showMonths, setShowMonths] = useState(
    parsed ? parsed.months.length < 12 : false
  )

  const activeMonths = showMonths ? months : ALL_MONTHS
  const cronPayload = { frequency, hours, weekDays, monthDays, months: activeMonths }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      if (parsed)
        return
    }
    onChange(buildCron(cronPayload))
  }, [frequency, hours, weekDays, monthDays, activeMonths])

  const handleFrequencyChange = useCallback(option => setFrequency(option.value), [])
  const handleHoursChange = useCallback(options => setHours(toValues(options)), [])
  const handleWeekDaysChange = useCallback(options => setWeekDays(toValues(options)), [])
  const handleMonthDaysChange = useCallback(options => setMonthDays(toValues(options)), [])
  const handleMonthsChange = useCallback(options => setMonths(toValues(options)), [])
  const handleShowMonthsChange = useCallback(event => {
    setShowMonths(event.target.checked)
  }, [])

  const summary = useMemo(() => buildCronSummary(cronPayload), [
    frequency, hours, weekDays, monthDays, activeMonths
  ])

  const isWeekly = frequency === 'weekly'
  const dayOptions = isWeekly ? DAYS_OF_WEEK : MONTH_DAYS
  const dayValues = isWeekly ? weekDays : monthDays
  const handleDayChange = isWeekly ? handleWeekDaysChange : handleMonthDaysChange

  const hoursSelect = (
    <Select
      options={HOUR_OPTIONS}
      value={fromValues(hours, HOUR_OPTIONS)}
      closeMenuOnSelect={false}
      isMulti
      showSelectAll
      onChange={handleHoursChange}
    />
  )

  const renderDayHourFields = () => {
    if (frequency === 'daily') {
      return (
        <div className="form-group mb-3">
          <label>Heure d&apos;envoi</label>
          {hoursSelect}
        </div>
      )
    }
    return (
      <div className="form-group mb-3">
        <div className="form-row">
          <div className="col"><label>Jour d&apos;envoi</label></div>
          <div className="col-auto px-3 invisible">À</div>
          <div className="col"><label>Heure d&apos;envoi</label></div>
        </div>
        <div className="form-row align-items-start">
          <div className="col pr-0">
            <Select
              options={dayOptions}
              value={fromValues(dayValues, dayOptions)}
              closeMenuOnSelect={false}
              isMulti
              showSelectAll
              onChange={handleDayChange}
            />
          </div>
          <div className="col-auto px-3 py-2">À</div>
          <div className="col pl-0">{hoursSelect}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="custom-cron-builder p-3">
      <h3 className="h5 font-weight-normal mb-3">Récurrence personnalisée</h3>
      <div className="form-group mb-3">
        <label>Récurrence</label>
        <Select
          options={FREQUENCY_OPTIONS}
          value={FREQUENCY_OPTIONS.find(option => option.value === frequency)}
          isSearchable={false}
          onChange={handleFrequencyChange}
        />
      </div>
      {renderDayHourFields()}
      <label className={CHECKBOX_LABEL_CLASS}>
        <Checkbox checked={showMonths} onChange={handleShowMonthsChange} />
        <span className={CHECKBOX_TEXT_CLASS}>Limiter à certains mois de l&apos;année</span>
      </label>
      {showMonths ? (
        <div className="form-group mt-3">
          <label>Mois d&apos;envoi</label>
          <Select
            options={MONTH_NAMES}
            value={fromValues(months, MONTH_NAMES)}
            closeMenuOnSelect={false}
            isMulti
            showSelectAll
            onChange={handleMonthsChange}
          />
        </div>
      ) : null}
      {hours.length > 0 ? (
        <p className="text-info-blue mb-0 mt-3">
          <i className="fas fa-check-circle mr-2" style={{ fontSize: 12 }} />
          <small className="font-weight-semibold">{summary}</small>
        </p>
      ) : null}
    </div>
  )
}

CustomCronBuilder.propTypes = {
  initialCron: PropTypes.string,
  onChange: PropTypes.func.isRequired
}

CustomCronBuilder.defaultProps = {
  initialCron: null
}

export default CustomCronBuilder
